import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useEffectiveClientId } from '@/hooks/useAuth'
import { useBorrowStory, SEED_COSTS } from '@/hooks/useSeeds'
import { useNextPlan } from '@/hooks/useControls'
import { emmaEngine } from '@/components/emma/Emma'
import { useSeedView } from './SeedChip'
import { useChrome, setSeedPanel } from './chrome/chromeStore'
import { ROUTES } from './chrome/nav'

/**
 * What the seed meter has to say, said by Emma (Brian's topbar.js v3.1 to v3.6
 * and app-data.js v4.6 to v5): at most a few times a week.
 *   pace    once a quarter of the week is used and two days are gone, when this
 *           week's seeds run out before Sunday at this pace
 *   90      crossing 90% of the week
 *   100     the sheet: "This week's stories are used", Borrow one story from
 *           next week (once a week), Keep going or Add seeds, Wait for Monday;
 *           See plans only after two weeks at the wall
 *   refill  Monday, when they come back (with what rolled over)
 *   teach   once, on the account's first story: what a story uses
 * Emma says it from her corner (the tab bar, or her desktop dock); on a screen
 * without her (Record) a note drops in at the top. Tapping it opens the panel.
 */
type Say = { kind: 'pace'; day: string } | { kind: '90' } | { kind: 'refill'; rolled: number } | { kind: 'teach' }

interface Memory { week: string; steps: string[]; spent: boolean; taught: boolean; walls: string[] }

const key = (c: string) => `gg.meter.${c}`
function load(c: string): Memory | null {
  try { const raw = localStorage.getItem(key(c)); return raw ? JSON.parse(raw) as Memory : null } catch { return null }
}
function store(c: string, m: Memory) {
  try { localStorage.setItem(key(c), JSON.stringify(m)) } catch { /* private mode */ }
}

export function MeterVoice({ emmaShown }: { emmaShown: boolean }) {
  const clientId = useEffectiveClientId()
  const v = useSeedView()
  const navigate = useNavigate()
  const next = useNextPlan()
  const borrow = useBorrowStory()
  const bars = useChrome((s) => s.bars)
  const [say, setSay] = useState<Say | null>(null)
  const [wall, setWall] = useState(false)
  const last = useRef<{ pct: number; total: number } | null>(null)
  const mem = useRef<Memory | null>(null)

  // The week turns over: Monday's refill, and the notes start again.
  useEffect(() => {
    if (!clientId || !v.b) return
    const week = v.b.week_start
    const m = load(clientId)
    if (!m) {
      mem.current = { week, steps: [], spent: v.b.weekly_spent > 0, taught: false, walls: [] }
    } else if (m.week !== week) {
      mem.current = { ...m, week, steps: [], spent: v.b.weekly_spent > 0 }
      if (m.spent) window.setTimeout(() => setSay({ kind: 'refill', rolled: v.b?.rollover ?? 0 }), 2200)
    } else {
      mem.current = m
    }
    store(clientId, mem.current)
    // A new account was never taught on another device; the ledger says so.
    last.current = null
  }, [clientId, v.b?.week_start]) // eslint-disable-line react-hooks/exhaustive-deps

  // A spend: did it cross 90 or 100, or set a pace that runs out before Sunday?
  useEffect(() => {
    const m = mem.current
    if (!clientId || !v.b || !m) return
    const before = last.current
    last.current = { pct: v.pct, total: v.total }
    if (v.b.weekly_spent > 0 && !m.spent) { m.spent = true; store(clientId, m) }
    if (!before || v.total >= before.total) return
    const seen = m.steps
    let fired: Say | null = null
    let toWall = false
    if (v.pct >= 100 && before.pct < 100 && !seen.includes('100')) {
      seen.push('90', '100'); toWall = true
    } else if (v.pct >= 90 && before.pct < 90 && !seen.includes('90')) {
      seen.push('90'); fired = { kind: '90' }
    } else if (!seen.includes('pace') && v.pct >= 25 && v.runsOut && v.into >= 2) {
      seen.push('pace'); fired = { kind: 'pace', day: v.runsOut }
    }
    store(clientId, m)
    if (toWall) {
      m.walls = (m.walls ?? []).filter((w) => w !== m.week).concat(m.week).slice(-4)
      store(clientId, m)
      window.setTimeout(() => setWall(true), 1300)
      return
    }
    if (fired) { const f = fired; window.setTimeout(() => setSay(f), 1300); return }
    // The first story the account ever builds: Emma says once what a story uses.
    if (!m.taught && before.total - v.total >= SEED_COSTS.build_story) {
      void supabase.from('seed_ledger').select('ref_id', { count: 'exact', head: false })
        .eq('client_id', clientId).eq('action', 'build_story').lt('delta', 0).limit(3)
        .then(({ data }) => {
          const builds = new Set((data ?? []).map((r) => (r as { ref_id: string | null }).ref_id ?? 'x'))
          m.taught = true
          store(clientId, m)
          if (builds.size === 1) window.setTimeout(() => setSay({ kind: 'teach' }), 1300)
        })
    }
  }, [v.total, v.pct, clientId]) // eslint-disable-line react-hooks/exhaustive-deps

  // She talks while the line shows, then it goes.
  useEffect(() => {
    if (!say) return
    emmaEngine()?.ask()
    const t = window.setTimeout(() => setSay(null), emmaShown && bars ? 7500 : 6000)
    return () => window.clearTimeout(t)
  }, [say, emmaShown, bars])

  const n = v.weekStories
  const stories = `${n} ${n === 1 ? 'story' : 'stories'}`
  const after = v.purchased ? 'Your purchased seeds cover the rest of the week.' : 'They come back Monday.'
  function words(x: Say): ReactNode {
    if (x.kind === 'pace') return <><b>At this pace, this week’s seeds run out {x.day}.</b> {after}</>
    if (x.kind === '90') return n === 0
      ? <><b>Not quite enough for another story this week.</b> {after}</>
      : <><b>About {n === 1 ? 'one story' : stories} left this week.</b> {after}</>
    if (x.kind === 'refill') return x.rolled
      ? <><b>Your seeds are back for the week,</b> plus {x.rolled} you didn’t use last week.</>
      : <><b>Your seeds are back for the week.</b></>
    const c = SEED_COSTS.build_story
    return <><b>Your first story! A story uses {c} seeds,</b> about {Math.round((100 * c) / Math.max(1, v.week))}% of your week. Rerolls, rewrites, other positions and the newsletter version use a little too, all from the same week. I’ll tell you if you’re running low.</>
  }

  const ring = (pct: number) => {
    const c = 2 * Math.PI * 14, off = c * (1 - pct / 100)
    return (
      <svg className="gg-mnote__ring" viewBox="0 0 34 34" aria-hidden="true">
        <circle cx="17" cy="17" r="14" fill="none" stroke="#2c2a28" strokeWidth="4" />
        <circle cx="17" cy="17" r="14" fill="none" stroke={pct >= 90 ? 'var(--alert)' : 'var(--amber)'} strokeWidth="4" strokeLinecap="round"
          strokeDasharray={c.toFixed(1)} strokeDashoffset={off.toFixed(1)} transform="rotate(-90 17 17)" />
      </svg>
    )
  }

  const open = () => { setSay(null); setSeedPanel(true) }

  // The wall (topbar.js showWall).
  const m = mem.current
  const again = (m?.walls?.length ?? 0) >= 2 && next
  const days = v.backWhen === 'tomorrow' ? 'tomorrow' : `Monday, in ${Math.max(1, Math.ceil(7 - v.into))} days`
  const canBorrow = !!v.b && v.borrowed === 0 && v.weekly < SEED_COSTS.build_story
  const build = SEED_COSTS.build_story
  const closeWall = () => setWall(false)

  return (
    <>
      {say && (emmaShown && bars ? (
        <div className="gg-emsay" role="status" onClick={open}>
          <span className="gg-emsay__who">Emma</span>
          <div className="gg-emsay__t">{words(say)}</div>
          <span className="gg-emsay__go">Tap for your seeds</span>
        </div>
      ) : (
        <div className={`gg-mnote${v.pct >= 90 ? ' is-hot' : ''}`} role="status" onClick={open}>
          {ring(v.pct)}<div className="gg-mnote__t">{words(say)}</div>
        </div>
      ))}

      {wall && (
        <div className="gg-wall" role="dialog" aria-modal="true" aria-label="This week’s stories are used" onClick={(e) => { if (e.target === e.currentTarget) closeWall() }}>
          <div className="gg-wall__box">
            <h3>This week’s stories are used</h3>
            <p>Everything else keeps working: your feed, alerts, rerolls, rewrites and Emma. Only new stories wait.</p>
            {canBorrow && (
              <button type="button" className="gg-btn gg-btn--amber" disabled={borrow.isPending}
                onClick={() => { borrow.mutate(undefined, { onSettled: closeWall }) }}>
                Borrow one story from next week
              </button>
            )}
            {v.purchased > 0 ? (
              <>
                <p>After that, new stories come out of your purchased seeds: <b>{v.purchased}</b>, about {Math.floor(v.purchased / build)} stories. Build shows its price while they do. This week’s come back {days}.</p>
                {again && <p>That’s two weeks in a row at the limit. <b>{next!.name}</b> gives about {Math.floor(next!.seeds_week / build)} stories a week.</p>}
                <div className="gg-wall__row">
                  <button type="button" className={`gg-btn${canBorrow ? '' : ' gg-btn--amber'}`} onClick={closeWall}>Keep going</button>
                  <button type="button" className="gg-btn" onClick={closeWall}>Wait for Monday</button>
                </div>
              </>
            ) : (
              <>
                <p>They come back {days}. Until then you can add seeds{again ? ', or move up a plan' : ''}.</p>
                {again && <p>That’s two weeks in a row at the limit. <b>{next!.name}</b> gives about {Math.floor(next!.seeds_week / build)} stories a week.</p>}
                <div className="gg-wall__row">
                  <button type="button" className="gg-btn gg-btn--amber" onClick={() => { closeWall(); navigate(`${ROUTES.settings}?tab=seeds`) }}>Add seeds</button>
                  <button type="button" className="gg-btn" onClick={closeWall}>Wait for Monday</button>
                </div>
              </>
            )}
            {again && <button type="button" className="gg-btn" onClick={() => { closeWall(); navigate(`${ROUTES.settings}?tab=plan`) }}>See plans</button>}
          </div>
        </div>
      )}
    </>
  )
}
